package com.rosario.juridico.domain

import com.rosario.juridico.marketplace.model.NivelVerificacion
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext

/**
 * Resultado de operaciones sobre el Directorio Público de Abogados.
 */
sealed class DirectorioResult<out T> {
    data class Success<out T>(val data: T) : DirectorioResult<T>()
    data class Error(val message: String, val cause: Throwable? = null) : DirectorioResult<Nothing>()
}

/**
 * Repositorio exclusivo para consultar el Directorio Público de Abogados Verificados (Estilo AVVO).
 * Filtra estrictamente profesionales con estado NIVEL_3_VERIFICADO.
 */
class DirectorioAbogadosRepository {

    /**
     * Consulta el directorio de abogados en Firestore filtrando únicamente aquellos que cuentan con NIVEL_3_VERIFICADO.
     * Permite filtrado multinivel por especialidad, localidad y búsqueda libre.
     */
    suspend fun getAbogadosVerificados(
        especialidad: String? = null,
        localidad: String? = null,
        queryBusqueda: String? = null
    ): DirectorioResult<List<PerfilAbogadoDataModel>> = withContext(Dispatchers.Default) {
        try {
            val firestore = Firebase.firestore
            val collectionRef = firestore.collection("perfiles_abogados")
            val snapshot = collectionRef.get()

            val abogadosVerificados = snapshot.documents
                .mapNotNull { doc ->
                    try {
                        doc.data<PerfilAbogadoDataModel>().copy(abogadoUid = doc.id)
                    } catch (e: Exception) {
                        val mat = try { doc.get<String?>("matricula") } catch (_: Exception) { null }
                        val tel = try { doc.get<String?>("telefono") } catch (_: Exception) { null }
                        val nom = try { doc.get<String?>("nombreCompleto") } catch (_: Exception) { null }
                        val jur = try { doc.get<String?>("jurisdiccion") } catch (_: Exception) { null }
                        val bio = try { doc.get<String?>("biografia") } catch (_: Exception) { null }
                        val niv = try { doc.get<String?>("nivelVerificacion") } catch (_: Exception) { null }
                        PerfilAbogadoDataModel(
                            abogadoUid = doc.id,
                            nombreCompleto = nom.orEmpty().ifBlank { "Dr./Dra. Profesional" },
                            matricula = mat,
                            telefono = tel,
                            jurisdiccion = jur.orEmpty().ifBlank { "Rosario" },
                            biografia = bio.orEmpty(),
                            nivelVerificacion = niv ?: NivelVerificacion.NIVEL_1_CREADA.name
                        )
                    }
                }
                // 🔒 REGLA TRUST & SAFETY: Filtrar estrictamente abogados verificados (NIVEL 3)
                .filter { it.nivelVerificacion == NivelVerificacion.NIVEL_3_VERIFICADO.name }
                // 🎯 Filtro por Especialidad
                .filter { item ->
                    especialidad.isNullOrBlank() || especialidad == "Todas" ||
                            item.especialidades.any { it.equals(especialidad, ignoreCase = true) || it.contains(especialidad, ignoreCase = true) }
                }
                // 📍 Filtro por Localidad / Jurisdicción
                .filter { item ->
                    localidad.isNullOrBlank() || localidad == "Todas" ||
                            item.jurisdiccion.contains(localidad, ignoreCase = true)
                }
                // 🔍 Búsqueda libre por texto (Nombre, biografía, especialidades)
                .filter { item ->
                    queryBusqueda.isNullOrBlank() ||
                            item.nombreCompleto.contains(queryBusqueda, ignoreCase = true) ||
                            item.especialidades.any { it.contains(queryBusqueda, ignoreCase = true) } ||
                            item.jurisdiccion.contains(queryBusqueda, ignoreCase = true) ||
                            item.biografia.contains(queryBusqueda, ignoreCase = true)
                }
                .sortedBy { it.nombreCompleto }

            DirectorioResult.Success(abogadosVerificados)
        } catch (e: Exception) {
            DirectorioResult.Error(
                message = e.message ?: "Error al obtener el directorio público de abogados.",
                cause = e
            )
        }
    }
}
